import { InfoCircleFilled } from "@ant-design/icons";
import { gql } from "@apollo/client";
import { Button, Form, Input, message, Modal, Spin } from "antd";
import DisplayNameListPopover from "components/bulk-edit-material/display-name-list";
import { SidepanelStyles } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import { MUTATION_BULK_EDIT_FEATURE_INSTANCES } from "entity-app/graphQL/ciq-feature-mutations";
import { QUERY_PLANNING_SUBMITTAL_OFFSETS } from "entity-app/graphQL/ciq-feature-queries";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { TMaterialLog } from "pages/material-log-feature/model";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { QUERY_WORKFLOW_DEFAULT } from "services/graphQL/queries";
import { restrictInputToNumbers } from "utils/inpututils";
import {
  buildPlanOffsetInputs,
  buildSubmittalPlanBulkEditVariables,
  canSaveSubmittalPlanBulk,
  categorizeMaterialsForSubmittalPlan,
  durationFieldValue,
  getEffectiveDuration,
  getSubmittalPlanLinkedItem,
  getSystemDefaultPlanDurations,
  parsePlanOffsetDetails,
  parsePositiveIntegerInput,
  PlanOffsetDetail,
  resolveDurationPlaceholder
} from "./helpers";

type Props = {
  selectedRows: TMaterialLog[];
  showEdit: boolean;
  setShowEdit: (v: any) => void;
  onUpdateDone: () => void;
};

function materialNoun(count: number) {
  return count === 1 ? "Material" : "Materials";
}

function SummaryLine({
  count,
  list,
  text
}: Readonly<{
  count: number;
  list: Array<{ id: string; name: string }>;
  text: string;
}>) {
  // Both call sites are already gated on `count > 0` (summaryLineEntries), so a
  // zero-count line is never constructed.
  /* v8 ignore next */
  if (count === 0) return null;
  return (
    <>
      <DisplayNameListPopover
        list={list}
        placement="left"
        className="cursor-pointer"
      >
        <strong className="underline">{count}</strong>
      </DisplayNameListPopover>{" "}
      {text}
    </>
  );
}

function DaysInput({
  value,
  onChange,
  testId
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  testId: string;
}) {
  return (
    <Input
      className="w-full"
      type="number"
      min={0}
      suffix="Days"
      value={value ?? 0}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "") {
          onChange(0);
          return;
        }
        const num = Number(raw);
        onChange(Number.isFinite(num) && num >= 0 ? Math.floor(num) : 0);
      }}
      onBlur={(e) => {
        if (e.target.value === "") {
          onChange(0);
        }
      }}
      onKeyDown={restrictInputToNumbers}
      data-testid={testId}
    />
  );
}

function submittalPlanSuccessMessage(
  createdCount: number,
  updatedCount: number
) {
  if (createdCount > 0 && updatedCount > 0) {
    return `${createdCount + updatedCount} Submittal Plans created successfully`;
  }
  if (createdCount > 0) {
    return `${createdCount} Submittal Plan${createdCount === 1 ? "" : "s"} created`;
  }
  if (updatedCount > 0) {
    return `${updatedCount} Submittal Plan${updatedCount === 1 ? "" : "s"} updated`;
  }
  return null;
}

function BulkFeatureEditSubmittalPlan(props: Readonly<Props>) {
  const { selectedRows, showEdit, setShowEdit, onUpdateDone } = props;
  const { gqlClientForProject } = useContext(ProjectContext);

  const [inProgress, setInProgress] = useState(false);
  const applyInFlightRef = useRef(false);
  const [prepInput, setPrepInput] = useState<number | null>(null);
  const [reviewInput, setReviewInput] = useState<number | null>(null);
  const [durationsSeeded, setDurationsSeeded] = useState(false);

  const categorized = useMemo(
    () => categorizeMaterialsForSubmittalPlan(selectedRows),
    [selectedRows]
  );

  const planIdByMaterial = useMemo(() => {
    const map: Record<string, string> = {};
    const updateIds = new Set(categorized.hasSubmittalPlan.map((m) => m.id));
    selectedRows.forEach((row) => {
      if (!updateIds.has(row.id)) return;
      const plan = getSubmittalPlanLinkedItem(row);
      if (plan?.id) map[row.id] = plan.id;
    });
    return map;
  }, [selectedRows, categorized.hasSubmittalPlan]);

  const planIds = useMemo(
    () => Object.values(planIdByMaterial),
    [planIdByMaterial]
  );

  const materialIdByPlanId = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(planIdByMaterial).forEach(([materialId, planId]) => {
      map[planId] = materialId;
    });
    return map;
  }, [planIdByMaterial]);

  const { data: planOffsetQueryData, loading: planOffsetsLoading } =
    useCIQQuery<{
      feature_instance: any[];
    }>(QUERY_PLANNING_SUBMITTAL_OFFSETS, {
      client: gqlClientForProject,
      skip: !gqlClientForProject || planIds.length === 0 || !showEdit,
      variables: { ids: planIds },
      fetchPolicy: "network-only"
    });

  const planOffsetDetails: PlanOffsetDetail[] = useMemo(
    () =>
      parsePlanOffsetDetails(
        planOffsetQueryData?.feature_instance,
        materialIdByPlanId
      ),
    [planOffsetQueryData?.feature_instance, materialIdByPlanId]
  );

  const { data: workflowDefaultData, loading: defaultsLoading } = useCIQQuery<{
    workflow_defaults: Array<{
      feature_id: number;
      workflow_data: Array<{ offset_name: string; offset_value?: number }>;
    }>;
  }>(QUERY_WORKFLOW_DEFAULT, {
    client: gqlClientForProject,
    skip: !gqlClientForProject || !showEdit,
    variables: {
      where: { feature_id: { _eq: FeatureTypes.SUBMITTALS } }
    }
  });

  const systemDefaults = useMemo(
    () => getSystemDefaultPlanDurations(workflowDefaultData?.workflow_defaults),
    [workflowDefaultData?.workflow_defaults]
  );

  const placeholderOptions = useMemo(
    () => ({
      hasAnyPlan: categorized.hasSubmittalPlan.length > 0,
      isMixedSelection:
        categorized.hasSubmittalPlan.length > 0 &&
        categorized.noSubmittalPlan.length > 0
    }),
    [categorized.hasSubmittalPlan.length, categorized.noSubmittalPlan.length]
  );

  const prepPlaceholder = useMemo(() => {
    const durations = categorized.hasSubmittalPlan.map((m) => {
      const detail = planOffsetDetails.find((d) => d.materialId === m.id);
      return detail?.prepDuration;
    });
    return resolveDurationPlaceholder(durations, {
      ...placeholderOptions,
      systemDefault: systemDefaults.prep
    });
  }, [
    categorized.hasSubmittalPlan,
    planOffsetDetails,
    placeholderOptions,
    systemDefaults.prep
  ]);

  const reviewPlaceholder = useMemo(() => {
    const durations = categorized.hasSubmittalPlan.map((m) => {
      const detail = planOffsetDetails.find((d) => d.materialId === m.id);
      return detail?.reviewDuration;
    });
    return resolveDurationPlaceholder(durations, {
      ...placeholderOptions,
      systemDefault: systemDefaults.review
    });
  }, [
    categorized.hasSubmittalPlan,
    planOffsetDetails,
    placeholderOptions,
    systemDefaults.review
  ]);

  const loading = (planIds.length > 0 && planOffsetsLoading) || defaultsLoading;

  useEffect(() => {
    if (!showEdit) {
      setPrepInput(null);
      setReviewInput(null);
      setInProgress(false);
      setDurationsSeeded(false);
    }
  }, [showEdit, selectedRows]);

  useEffect(() => {
    if (!showEdit || loading || durationsSeeded) return;
    setPrepInput(durationFieldValue(prepPlaceholder) ?? 0);
    setReviewInput(durationFieldValue(reviewPlaceholder) ?? 0);
    setDurationsSeeded(true);
  }, [showEdit, loading, durationsSeeded, prepPlaceholder, reviewPlaceholder]);

  const [bulkEditFeatureInstances] = useCIQMutation(
    gql(MUTATION_BULK_EDIT_FEATURE_INSTANCES),
    { client: gqlClientForProject }
  );

  const prepField = parsePositiveIntegerInput(prepInput);
  const reviewField = parsePositiveIntegerInput(reviewInput);

  const canSave = canSaveSubmittalPlanBulk({
    createCount: categorized.noSubmittalPlan.length,
    updateCount: categorized.hasSubmittalPlan.length,
    prepField,
    reviewField,
    prepPlaceholder,
    reviewPlaceholder
  });

  const onCancel = () => {
    setShowEdit(null);
  };

  const runCreateStep = useCallback(async (): Promise<number | null> => {
    const materials = categorized.noSubmittalPlan;
    if (materials.length === 0) return 0;

    const prep = getEffectiveDuration(prepField, prepPlaceholder, "create");
    const review = getEffectiveDuration(
      reviewField,
      reviewPlaceholder,
      "create"
    );
    if (prep === null || review === null) {
      message.error("Prep Time and Review Time are required to create plans.");
      return null;
    }

    const response: any = await bulkEditFeatureInstances({
      variables: buildSubmittalPlanBulkEditVariables(
        materials.map((m) => m.id),
        buildPlanOffsetInputs(prep, review)
      )
    });
    if (!response?.success) {
      message.error("Unable to create Submittal Plan(s).");
      return null;
    }
    return materials.length;
  }, [
    categorized.noSubmittalPlan,
    prepField,
    reviewField,
    prepPlaceholder,
    reviewPlaceholder,
    bulkEditFeatureInstances
  ]);

  const runUpdateStep = useCallback(
    async (createdCount: number): Promise<number | null> => {
      const materials = categorized.hasSubmittalPlan;
      if (materials.length === 0) return 0;

      const offsets = buildPlanOffsetInputs(prepField, reviewField);
      if (offsets.length === 0) {
        if (createdCount > 0) return 0;
        message.error("No Submittal Plan durations could be applied.");
        return null;
      }

      const response: any = await bulkEditFeatureInstances({
        variables: buildSubmittalPlanBulkEditVariables(
          materials.map((m) => m.id),
          offsets
        )
      });
      if (!response?.success) {
        message.error("Unable to update Submittal Plan(s).");
        return null;
      }
      return materials.length;
    },
    [
      categorized.hasSubmittalPlan,
      prepField,
      reviewField,
      bulkEditFeatureInstances
    ]
  );

  const onApply = useCallback(async () => {
    if (!canSave || applyInFlightRef.current) return;
    applyInFlightRef.current = true;
    setInProgress(true);

    let createdCount = 0;
    let updatedCount = 0;

    try {
      const created = await runCreateStep();
      if (created === null) return;
      createdCount = created;

      const updated = await runUpdateStep(createdCount);
      if (updated === null) {
        if (createdCount > 0) onUpdateDone();
        return;
      }
      updatedCount = updated;

      const successText = submittalPlanSuccessMessage(
        createdCount,
        updatedCount
      );
      if (successText) message.success(successText);
      if (createdCount > 0 || updatedCount > 0) onUpdateDone();
    } catch {
      message.error("Failed to apply Submittal Plan durations.");
      if (createdCount > 0) onUpdateDone();
    } finally {
      applyInFlightRef.current = false;
      setInProgress(false);
    }
  }, [canSave, runCreateStep, runUpdateStep, onUpdateDone]);

  const showDurationForm =
    categorized.hasSubmittalPlan.length > 0 ||
    categorized.noSubmittalPlan.length > 0;

  const excludedCount = categorized.linkedToSubmittal.length;
  const updateCount = categorized.hasSubmittalPlan.length;
  const showSummary = excludedCount > 0 || updateCount > 0;

  const summaryLineEntries: Array<{ key: string; node: JSX.Element }> = [
    excludedCount > 0
      ? {
          key: "excluded",
          node: (
            <SummaryLine
              count={excludedCount}
              list={categorized.linkedToSubmittal}
              text={`${materialNoun(excludedCount)} will be excluded as Submittals are linked.`}
            />
          )
        }
      : null,
    updateCount > 0
      ? {
          key: "update",
          node: (
            <SummaryLine
              count={updateCount}
              list={categorized.hasSubmittalPlan}
              text={`${materialNoun(updateCount)} with existing Submittal Plan will be updated with new durations.`}
            />
          )
        }
      : null
  ].filter((x): x is { key: string; node: JSX.Element } => x !== null);

  return (
    <Modal
      title={
        <div data-testid="bulk-edit-submittal-plan-title">
          <span>Create Submittal Plan</span>
          <span className="text-xs font-normal ml-2 mr-2">|</span>
          <span className="text-xs font-normal">
            {selectedRows.length} Selected Materials
          </span>
        </div>
      }
      className="custom-drawer"
      style={SidepanelStyles.style}
      width={420}
      open={showEdit}
      onCancel={onCancel}
      destroyOnHidden
      data-testid="bulk-edit-submittal-plan-modal"
      footer={
        <div className="flex w-full space-x-3 justify-end">
          <Button
            onClick={onCancel}
            disabled={inProgress}
            data-testid="bulk-edit-submittal-plan-cancel"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!canSave || inProgress || loading}
            loading={inProgress}
            onClick={onApply}
            data-testid="bulk-edit-submittal-plan-apply"
          >
            Apply
          </Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        <div
          className="body-content-wrapper"
          data-testid="bulk-edit-submittal-plan-body"
        >
          {showSummary && (
            <div
              style={{
                backgroundColor: "var(--ciq-blue-100)",
                border: "1px solid var(--ciq-blue-300)",
                borderRadius: "6px",
                padding: "12px 10px",
                marginBottom: "16px",
                display: "flex",
                alignItems: "flex-start"
              }}
              data-testid="bulk-edit-submittal-plan-summary"
            >
              <div>
                <InfoCircleFilled className="text-ciq-blue-700 pr-[5px]" />
              </div>
              <div>
                {summaryLineEntries.length > 1 ? (
                  <>
                    <span className="font-semibold">Please Note:</span>
                    <ul className="list-disc pl-4 mb-0">
                      {summaryLineEntries.map(({ key, node }) => (
                        <li key={key}>{node}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  summaryLineEntries[0]?.node
                )}
              </div>
            </div>
          )}

          {showDurationForm && (
            <Form layout="vertical" className="w-[95%]">
              <Form.Item label="Prep Time" className="mb-3">
                <DaysInput
                  value={prepInput}
                  onChange={setPrepInput}
                  testId="bulk-edit-submittal-plan-prep"
                />
              </Form.Item>
              <Form.Item label="Review Time" className="mb-0">
                <DaysInput
                  value={reviewInput}
                  onChange={setReviewInput}
                  testId="bulk-edit-submittal-plan-review"
                />
              </Form.Item>
            </Form>
          )}
        </div>
      </Spin>
    </Modal>
  );
}

export default BulkFeatureEditSubmittalPlan;
